// Basic dialog built on @floating-ui/react's FloatingFocusManager + FloatingOverlay.
// Esc to close, backdrop click to close, focus trapped while open. Portaled to <body>.
// Radius follows DESIGN-SYSTEM.md §6 (cards/panels/modals → rounded-xl).

import { ReactNode } from "react";
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from "@floating-ui/react";

export interface ModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: string;
  readonly children: ReactNode;
}

export function Modal({ open, onClose, title, children }: ModalProps) {
  const { refs, context } = useFloating({
    open,
    onOpenChange: (v) => {
      if (!v) onClose();
    },
  });

  const click = useClick(context, { enabled: false }); // opened programmatically
  const dismiss = useDismiss(context, { escapeKey: true, outsidePress: true });
  const role = useRole(context, { role: "dialog" });

  const { getFloatingProps } = useInteractions([click, dismiss, role]);

  if (!open) return null;

  return (
    <FloatingPortal>
      <FloatingOverlay
        lockScroll
        className="au-overlay-in z-overlay flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      >
        <FloatingFocusManager context={context}>
          <div
            ref={refs.setFloating}
            className="au-panel-in w-full max-w-md rounded-xl border border-slate-800 bg-slate-900 p-6 shadow-2xl shadow-black/50"
            aria-labelledby="modal-title"
            {...getFloatingProps()}
          >
            <h2 id="modal-title" className="mb-4 text-lg font-semibold text-slate-100">
              {title}
            </h2>
            {children}
          </div>
        </FloatingFocusManager>
      </FloatingOverlay>
    </FloatingPortal>
  );
}
